"use client";
import { DayViewCalendar } from "@cmp-src/calendar/CalendarDayView/CalendarDayView";
import { UserChip } from "@cmp-src/data-display/chips/UserChip/UserChip";
import { UserOldIcon } from "@cmp-src/icons/icons";
import { formatInTimeZone } from "date-fns-tz";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";

import { SelectUsersAsync } from "@/features/users/components/SelectUsersAsync";
import { useReserveModal } from "@/features/reserve/hooks/useReserveModal";
import { GetCalendarResponse, Office } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { useMyCalendar } from "@gettactic/hooks";
import { Button } from "@cmp-src/primitives/button";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import { useOptionalReserveForm } from "@cmp-src/reserve/ReverseFormContext";
import { useMap } from "@/features/map/MapProvider";
import Link from "next/link";
import { ControlProps, GroupBase, MultiValue, components } from "react-select";
import { mapMyCalendarToCalendarEvents } from "./myCalendarUtils";
import { SETTINGS_ROUTES } from "@gettactic/defines/src/web-routes";
import { buildUrl } from "@gettactic/helpers";

export interface MapDailyViewCardProps {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  office?: Office;
  isModal?: boolean;
  isReserving?: boolean;
}

const classes = {
  Wrapper:
    "absolute top-4 bottom-4 left-[410px] flex w-[368px] flex-col rounded-brand border bg-[#F7F9FB] shadow-componentCard border-0",
  Container:
    "flex h-full flex-col pl-4 border border-neutral-200 rounded-brand",
  CalendarWrapper: "relative w-full flex-1 px-4 pr-4",
  TagsInputLeftIcon: "size-4 text-neutral-600",
  TagsInputWrapper: "rounded-[8px] hover:bg-neutral-100",
  TagsInputContainer: "v2-reservation w-full p-5 px-5",
  Calendar: "absolute inset-0 border-0",
  NoIntegrations:
    "w-full h-full rounded-[8px] absolute bg-background opacity-50 p-4 flex flex-col items-center justify-center",
  NoIntegrationsContent:
    "flex flex-col items-center justify-center gap-2 rounded-[8px] p-4 m-4 text-center gap-6"
};

const TransparentRoundedControl = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>(
  props: ControlProps<Option, IsMulti, Group>
) => {
  const { className, ...rest } = props;

  return (
    <components.Control
      {...rest}
      className={cn(
        className,
        "!shadow-none !outline-none relative overflow-visible"
      )}
    >
      <div
        className={
          "rainbow-stable !rounded-brand !border !border-neutral-100 relative flex w-full flex-row items-center bg-primary-background px-2 transition-colors focus-within:outline-none hover:bg-neutral-100 focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-transparent"
        }
        style={{
          minHeight: "2.5rem",
          margin: 0,
          zIndex: 1
        }}
      >
        <UserOldIcon className={"size-4 text-neutral-600"} />
        {props.children}
      </div>
    </components.Control>
  );
};

export const MapDailyViewCard: React.FC<MapDailyViewCardProps> = ({
  isOpen,
  onOpenChange,
  office,
  isModal,
  isReserving
}) => {
  const all = useMap();
  const {
    selectedUsersInCalendarIntegrations: selectedUsers,
    setSelectedUsersInCalendarIntegrations: setSelectedUsers
  } = all;
  const { isBreakpoint } = useBreakpoint();

  const reserve = useOptionalReserveForm();
  const watchedUsers = reserve?.form?.watch("users");
  const usersValues = (
    reserve?.form ? watchedUsers || [] : (selectedUsers as never)
  ) as {
    value: string;
    label: string;
    picture?: string;
    profile_photo_url?: string;
  }[];

  const selectedUserIds =
    usersValues.length > 0 ? usersValues.map((user) => user?.value) : undefined;

  const isMd = isBreakpoint("md");

  // Use reserve form's startTime if available (respects selected date in modal),
  // otherwise fall back to map's startDate
  const effectiveDate = reserve?.startTime || all.startDate;

  const { data: calendar } = useMyCalendar({
    tz: all.currentOffice.time_zone,
    date: effectiveDate
      ? formatInTimeZone(
          effectiveDate,
          all.currentOffice.time_zone,
          "yyyy-MM-dd"
        )
      : undefined,
    enabled: all && isOpen,
    user_id: selectedUserIds
  });
  const formattedCalendar = React.useMemo(() => {
    return calendar
      ? mapMyCalendarToCalendarEvents(calendar as GetCalendarResponse)
      : [];
  }, [calendar]);

  const isNoIntegrations = React.useMemo(() => {
    return !calendar?.has_google_access && !calendar?.has_microsoft_access;
  }, [calendar]);

  const {
    editModalData,
    setEditModalData,
    cancelModalData,
    reserveModalData,
    setCancelModalData
  } = useReserveModal();

  const weekDates = reserve?.form?.watch("weekDates") as Date[] | undefined;
  const isMeetingRoom = reserve?.resourceType === "meeting_room";

  // For meeting rooms, the single selected date (weekDates[0]) is the source of truth.
  // For desks (which may have multiple dates), use the effective startTime so
  // the day view and map follow the main reservation date/time instead of
  // an arbitrary element from the dates array.
  const reserveCalendarDate = isMeetingRoom
    ? (weekDates?.[0] ?? reserve?.startTime ?? undefined)
    : (weekDates?.[0] ?? undefined);

  // Sync global users to form if form is empty on mount or when global changes
  React.useEffect(() => {
    if (reserve?.form && selectedUsers.length > 0) {
      const currentFormUsers = reserve.form.getValues("users") || [];
      if (currentFormUsers.length === 0) {
        reserve.form.setValue("users", selectedUsers as never);
      }
    }
  }, [reserve?.form, selectedUsers]);

  // Sync form users back to global state
  React.useEffect(() => {
    if (reserve?.form && watchedUsers) {
      setSelectedUsers(watchedUsers as never);
    }
  }, [watchedUsers, reserve?.form, setSelectedUsers]);

  const content = (
    <>
      <div className={classes.Container}>
        <div className={classes.TagsInputContainer}>
          {!!calendar && isNoIntegrations ? (
            <Button variant="outline" size="lg" className="w-full rounded-full">
              <Link href={buildUrl(SETTINGS_ROUTES.integrations)}>
                <p className="">Connect your calendar</p>
              </Link>
            </Button>
          ) : (
            <>
              <SelectUsersAsync
                onChange={(value) => {
                  setSelectedUsers((value as never) || []);
                  reserve?.form?.setValue("users", (value as never) || []);
                }}
                value={usersValues as never}
                className="w-full rounded-md border-neutral-300"
                classNamePrefix="react-select"
                isMulti
                placeholder={"Search for people"}
                managed={false}
                components={{
                  Control: TransparentRoundedControl
                }}
              />
              {usersValues.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {usersValues.map((item) => {
                    if (!item) return null;
                    const onCancel = (event: React.MouseEvent) => {
                      event.stopPropagation();
                      event.preventDefault();

                      const nextUsers = usersValues.filter(
                        (user) => user?.value !== item.value
                      );
                      setSelectedUsers(nextUsers as never);
                      reserve?.form?.setValue("users", nextUsers as never, {
                        shouldValidate: true
                      });
                    };
                    return (
                      <UserChip
                        key={item.value}
                        user={{
                          name: item.label,
                          profile_photo_url: item.profile_photo_url
                        }}
                        onClick={onCancel}
                        onCancel={onCancel}
                        useColoredBorder
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        <div className={classes.CalendarWrapper}>
          <DayViewCalendar
            currentDate={
              reserveCalendarDate ??
              reserveModalData?.start ??
              all.startDate ??
              undefined
            }
            events={isNoIntegrations ? [] : formattedCalendar}
            className={classes.Calendar}
            office={office}
            editModalData={editModalData}
            setEditModalData={setEditModalData}
            cancelModalData={cancelModalData}
            setCancelModalData={setCancelModalData}
            isReserving={isReserving}
          />
        </div>
      </div>
    </>
  );

  if (!isMd) {
    return (
      <MobileDrawer
        size={"full"}
        dragOnTileOnly
        isOpen={isOpen || false}
        onOpenChange={(value) => onOpenChange?.(value)}
        scrollableContent={false}
        containerClassName={"relative flex h-full"}
      >
        <div className={"relative w-full"}>{content}</div>
      </MobileDrawer>
    );
  }
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: -400 }} // Enter from left
          animate={{ x: 0 }} // Centered
          exit={{ x: -400 }} // Exit to left
          transition={{ duration: 0.2 }}
          className={cn(classes.Wrapper, { "top-0 bottom-0": isModal })}
        >
          {content}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
